import React, { useEffect, useMemo, useState } from "react";


const STRIDE = [
  { key: "S", name: "Spoofing", help: "Identität vortäuschen (z. B. als anderer Nutzer auftreten)" },
  { key: "T", name: "Tampering", help: "Daten/Transaktionen manipulieren" },
  { key: "R", name: "Repudiation", help: "Abstreitbarkeit, fehlende Nachvollziehbarkeit (Logging/Audit fehlt)" },
  { key: "I", name: "Information Disclosure", help: "Unbefugtes Offenlegen von Informationen" },
  { key: "D", name: "Denial of Service", help: "Dienst stören/überlasten" },
  { key: "E", name: "Elevation of Privilege", help: "Mehr Rechte erlangen als vorgesehen" },
];

const METHODS = [
  { key: "workshop", label: "Interviews / Workshop", short: "Menschenwissen" },
  { key: "checklist", label: "Checklisten", short: "Standardfragen" },
  { key: "scan", label: "Schwachstellen-Scan / Pentest", short: "Technikcheck" },
  { key: "incidents", label: "Analyse früherer Vorfälle", short: "Was war schon mal?" },
  { key: "process", label: "Prozess- / Workflow-Analyse", short: "Abläufe prüfen" },
  { key: "tm", label: "Threat Modeling (STRIDE / Attack Trees)", short: "systematisch" },
];

// Szenario-Daten (didaktisch; ohne technische Exploit-Anleitungen)
const SCENARIOS = [
  {
    key: "webshop",
    name: "Online‑Shop (Kundendaten + Zahlung)",
    crownJewels: ["Kundendaten", "Zahlungsprozess", "Admin‑Bereich", "Bestelldatenbank", "Produktpreise"],
    risks: [
      {
        id: "r-account-takeover",
        title: "Kontoübernahme",
        desc: "Angreifer melden sich als echte Nutzer an (z. B. durch geleakte Zugangsdaten).",
        stride: "S",
        foundBy: ["workshop", "incidents", "checklist", "tm"],
        note: "Typisch, wenn Logins häufig und Accounts wertvoll sind.",
      },
      {
        id: "r-input-validation",
        title: "Unzureichende Eingabeprüfung",
        desc: "Eingaben werden nicht sauber geprüft → kann zu Datenabfluss oder Manipulation führen.",
        stride: "I",
        foundBy: ["scan", "tm"],
        note: "Oft findet man Hinweise durch Scans/Pentests und Threat Modeling.",
      },
      {
        id: "r-price-manipulation",
        title: "Preis-/Warenkorb‑Manipulation",
        desc: "Preis, Rabatt oder Warenkorb‑Daten können unbemerkt verändert werden.",
        stride: "T",
        foundBy: ["process", "workshop", "tm"],
        note: "Prozessanalyse zeigt häufig, wo Prüfungen fehlen.",
      },
      {
        id: "r-weak-logging",
        title: "Schwaches Logging / keine Nachvollziehbarkeit",
        desc: "Wichtige Aktionen sind nicht sauber protokolliert → schwer zu klären, was passiert ist.",
        stride: "R",
        foundBy: ["checklist", "process", "tm"],
        note: "Checklisten decken so etwas zuverlässig auf.",
      },
      {
        id: "r-ddos",
        title: "Überlastung des Checkouts",
        desc: "Checkout oder Shop ist zeitweise nicht erreichbar (Überlast, Angriff, Fehlkonfiguration).",
        stride: "D",
        foundBy: ["incidents", "workshop", "tm"],
        note: "Vergangene Vorfälle liefern hier oft die besten Hinweise.",
      },
      {
        id: "r-admin-misconfig",
        title: "Zu hohe Rechte / falsche Rollen",
        desc: "Rollen sind zu großzügig → normale Nutzer bekommen Admin‑ähnliche Möglichkeiten.",
        stride: "E",
        foundBy: ["process", "workshop", "tm"],
        note: "Rollen/Prozesse sind oft der Kern des Problems.",
      },
    ],
    attackTree: {
      goalLabel: "Ziel: Kundendaten abgreifen",
      question: "Welche Pfade sind in diesem Szenario grundsätzlich plausibel (auf Verständnisebene)?",
      paths: [
        {
          id: "p1",
          label: "Unzureichende Eingabeprüfung → ungewollter Zugriff auf Daten",
          relatedRiskIds: ["r-input-validation"],
          correct: true,
          why: "Wenn Eingaben unsicher verarbeitet werden, kann das zu Datenabfluss führen.",
        },
        {
          id: "p2",
          label: "Kontoübernahme → Zugriff auf personenbezogene Daten im Konto",
          relatedRiskIds: ["r-account-takeover"],
          correct: true,
          why: "Wenn Accounts übernommen werden, sind Profildaten/Bestellhistorie oft sichtbar.",
        },
        {
          id: "p3",
          label: "Preis‑Manipulation → automatisch vollständige Kundendatenbank herunterladen",
          relatedRiskIds: ["r-price-manipulation"],
          correct: false,
          why: "Preismanipulation betrifft meist Transaktionen; direkter Datenbank‑Download ist nicht der typische Effekt.",
        },
      ],
    },
  },

  {
    key: "bank",
    name: "Online‑Banking‑Plattform (Kund:innenportal)",
    crownJewels: [
      "Login & MFA",
      "Kundendaten (Stammdaten)",
      "Kontostand & Umsätze",
      "Empfängerliste",
      "Überweisungen",
      "Audit‑Logs (Nachvollziehbarkeit)",
    ],
    risks: [
      {
        id: "b-account-takeover",
        title: "Kontoübernahme (Customer Account)",
        desc: "Ein fremder Dritter nutzt Zugangsdaten/Tricks, um sich als Kunde anzumelden.",
        stride: "S",
        foundBy: ["workshop", "incidents", "checklist", "tm"],
        note: "Sehr relevant, weil Identität im Online‑Banking zentral ist.",
      },
      {
        id: "b-transfer-tampering",
        title: "Manipulation von Überweisungsdaten",
        desc: "Überweisungsdetails (Empfänger/IBAN/Verwendungszweck) könnten unbemerkt verändert werden, wenn Kontrollen fehlen.",
        stride: "T",
        foundBy: ["process", "workshop", "tm"],
        note: "Prozessanalyse klärt: Welche Checks/Bestätigungen greifen wann?",
      },
      {
        id: "b-audit-gap",
        title: "Lücken in Audit‑Logs / Nachvollziehbarkeit",
        desc: "Es ist nicht eindeutig nachvollziehbar, wer eine kritische Aktion ausgelöst hat (z. B. Überweisung/Profiländerung).",
        stride: "R",
        foundBy: ["checklist", "process", "tm"],
        note: "Repudiation ist oft ein Logging‑ und Prozess‑Thema.",
      },
      {
        id: "b-info-disclosure",
        title: "Ungewollte Offenlegung von Kontoauszügen / Umsätzen",
        desc: "Falsche Berechtigungen oder Freigaben könnten dazu führen, dass andere Personen Daten sehen können.",
        stride: "I",
        foundBy: ["process", "checklist", "scan", "tm"],
        note: "Berechtigungen, Rollen und technische Prüfungen spielen zusammen.",
      },
      {
        id: "b-availability",
        title: "Ausfall der Plattform (z. B. zu Stoßzeiten)",
        desc: "Online‑Banking ist zeitweise nicht erreichbar (Überlast, Fehlkonfiguration, Angriffe).",
        stride: "D",
        foundBy: ["incidents", "workshop", "tm"],
        note: "Vorfälle zeigen häufig, wo Engpässe wirklich sind.",
      },
      {
        id: "b-privilege",
        title: "Zu hohe Rechte im Support/Admin‑Umfeld",
        desc: "Mitarbeiter‑ oder Systemrollen sind zu mächtig → es werden Änderungen möglich, die nicht vorgesehen sind.",
        stride: "E",
        foundBy: ["process", "workshop", "tm"],
        note: "Rollen & Freigaben sind klassische Privilege‑Risiken.",
      },
    ],
    attackTree: {
      goalLabel: "Ziel: Unberechtigte Überweisung auslösen",
      question: "Welche Pfade sind plausibel (ohne technische Details)?",
      paths: [
        {
          id: "q1",
          label: "Kontoübernahme → Überweisung im Namen des Kunden",
          relatedRiskIds: ["b-account-takeover"],
          correct: true,
          why: "Wenn Identität missbraucht wird, können Transaktionen ausgelöst werden (je nach Kontrollen).",
        },
        {
          id: "q2",
          label: "Zu hohe Rechte im Support/Admin‑Umfeld → kritische Änderungen → Überweisung wird möglich",
          relatedRiskIds: ["b-privilege"],
          correct: true,
          why: "Zu mächtige Rollen können Missbrauch oder Fehler ermöglichen.",
        },
        {
          id: "q3",
          label: "Plattform‑Ausfall → automatisch werden Überweisungen korrekt ausgeführt",
          relatedRiskIds: ["b-availability"],
          correct: false,
          why: "Ein Ausfall ist ein Verfügbarkeitsproblem – er führt nicht automatisch zu legitimen Transaktionen.",
        },
      ],
    },
  },
];

const css = `
/* Layout */
.ril { margin: 0; max-width: 1150px; }
.rilCard { border: 1px solid rgba(0,0,0,.12); border-radius: 14px; background: rgba(255,255,255,.92); padding: 14px; }
.rilHeader { display:flex; flex-wrap: wrap; gap: 12px; align-items: flex-start; justify-content: space-between; }
.rilTitle { margin: 0; font-size: 1.15rem; }
.rilSub { margin-top: 6px; opacity: .82; font-size: .95rem; max-width: 72ch; }
.rilRow { display:flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.rilSelect { padding: 8px 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.16); background: rgba(255,255,255,.95); }
.rilTabs { display:flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
.rilTabBtn { padding: 8px 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.16); background: rgba(255,255,255,.95); cursor:pointer; }
.rilTabBtnActive { background: rgba(0,0,0,.06); font-weight: 800; }

.rilGrid { display:grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 12px; }
@media (max-width: 980px){ .rilGrid { grid-template-columns: 1fr; } }

.rilPanel { border: 1px solid rgba(0,0,0,.12); border-radius: 14px; padding: 12px; background: rgba(0,0,0,.02); }
.rilPanelTitle { font-weight: 800; margin-bottom: 8px; }
.rilHint { font-size: .9rem; opacity: .8; margin-top: 6px; }

.rilMethods { display:grid; gap: 8px; }
.rilMethod { display:flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px; border-radius: 12px; border: 1px solid rgba(0,0,0,.12); background: rgba(255,255,255,.9); }
.rilMethodLeft { display:flex; flex-direction: column; gap: 2px; }
.rilMethodLabel { font-weight: 800; }
.rilMethodShort { font-size: .85rem; opacity: .75; }
.rilToggle { display:flex; align-items:center; gap: 8px; }
.rilBtn { padding: 7px 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.16); background: rgba(255,255,255,.95); cursor:pointer; }
.rilBtnPrimary { background: rgba(0,0,0,.06); font-weight: 900; }

.rilStats { display:grid; grid-template-columns: repeat(auto-fit, minmax(210px,1fr)); gap: 10px; margin-top: 10px; }
.rilStat { border: 1px solid rgba(0,0,0,.12); border-radius: 12px; padding: 10px; background: rgba(255,255,255,.9); }
.rilStatTitle { font-size: .9rem; opacity: .8; }
.rilStatValue { font-size: 1.15rem; font-weight: 900; margin-top: 4px; }

.rilBar { height: 10px; border-radius: 999px; background: rgba(0,0,0,.08); overflow:hidden; margin-top: 8px; }
.rilBarFill { height: 100%; background: rgba(0,0,0,.45); width: 0%; }

.rilList { list-style:none; margin:0; padding:0; display:grid; gap: 10px; }
.rilRisk { border: 1px solid rgba(0,0,0,.12); border-radius: 14px; padding: 10px 12px; background: rgba(255,255,255,.92); }
.rilRiskTop { display:flex; gap:10px; align-items:flex-start; justify-content: space-between; }
.rilRiskTitle { font-weight: 900; }
.rilRiskDesc { margin-top: 4px; opacity: .86; font-size: .93rem; }
.rilRiskMeta { margin-top: 6px; font-size: .85rem; opacity: .72; }
.rilPill { display:inline-block; padding: 3px 8px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12); background: rgba(0,0,0,.03); font-size: .82rem; }

.rilStrideBox { margin-top: 8px; display:grid; gap: 8px; }
.rilStrideOptions { display:flex; flex-wrap: wrap; gap: 6px; }
.rilOpt { padding: 6px 8px; border-radius: 10px; border: 1px solid rgba(0,0,0,.16); background: rgba(255,255,255,.95); cursor:pointer; font-size: .9rem; }
.rilOptActive { background: rgba(0,0,0,.06); font-weight: 900; }
.rilFeedbackOk { margin-top: 6px; font-size: .9rem; color: rgba(0,120,0,.95); }
.rilFeedbackBad { margin-top: 6px; font-size: .9rem; color: rgba(170,0,0,.95); }
.rilFeedbackNeutral { margin-top: 6px; font-size: .9rem; opacity: .78; }

.rilMini { margin-top: 10px; border-top: 1px dashed rgba(0,0,0,.18); padding-top: 10px; }
.rilStrideLegend { display:grid; gap: 8px; }
.rilLegendItem { border: 1px solid rgba(0,0,0,.12); border-radius: 12px; padding: 10px; background: rgba(255,255,255,.9); }
.rilLegendKey { font-weight: 900; margin-right: 6px; }
.rilCaption { margin-top: 10px; font-size: .9rem; opacity: .78; }

.rilLegendBottom { text-align: left; margin-top: 12px; border-top: 1px dashed rgba(0,0,0,.18); padding-top: 10px; }
.rilLegendBottom summary { cursor: pointer; font-weight: 900; }
.rilLegendBottom ul { margin: 10px 0 0 18px; }
.rilLegendBottom li { margin: 6px 0; }
`;

function uniq(arr) {
  return Array.from(new Set(arr));
}

function intersect(a, b) {
  const s = new Set(a);
  return b.some((x) => s.has(x));
}

export default function RiskIdentificationLab({
  title = "Risiko‑Detektiv: Risikoidentifikation in der Cybersecurity",
  
  initialScenario = "webshop",
}) {
  const [scenarioKey, setScenarioKey] = useState(initialScenario);
  const scenario = useMemo(
    () => SCENARIOS.find((s) => s.key === scenarioKey) ?? SCENARIOS[0],
    [scenarioKey]
  );

  const [tab, setTab] = useState("methods"); // methods | stride | tree
  const [selectedMethods, setSelectedMethods] = useState(() => new Set());
  const [strideAnswers, setStrideAnswers] = useState({}); // { riskId: "S".."E" }
  const [treeChoices, setTreeChoices] = useState({}); // { pathId: boolean }

  // Neu: STRIDE-Check / Lösung anzeigen
  const [strideChecked, setStrideChecked] = useState(false);
  const [strideReveal, setStrideReveal] = useState(false);

  // Reset beim Szenariowechsel
  useEffect(() => {
    setTab("methods");
    setSelectedMethods(new Set());
    setStrideAnswers({});
    setTreeChoices({});
    setStrideChecked(false);
    setStrideReveal(false);
  }, [scenarioKey]);

  const allRisks = scenario.risks;

  const discoveredRisks = useMemo(() => {
    const keys = Array.from(selectedMethods);

    // Threat Modeling aktiv: systematisch => alle Risiken sichtbar
    if (selectedMethods.has("tm")) return allRisks;

    return allRisks.filter((r) => intersect(r.foundBy, keys));
  }, [selectedMethods, allRisks]);

  const discoveredIds = useMemo(() => new Set(discoveredRisks.map((r) => r.id)), [discoveredRisks]);

  const progress = useMemo(() => {
    const total = allRisks.length;
    const found = discoveredRisks.length;
    const pct = total === 0 ? 0 : Math.round((found / total) * 100);

    const strideCovered = uniq(discoveredRisks.map((r) => r.stride)).length;

    const answered = Object.keys(strideAnswers).filter((id) => discoveredIds.has(id)).length;
    const correct = Object.entries(strideAnswers)
      .filter(([id]) => discoveredIds.has(id))
      .filter(([id, ans]) => allRisks.find((r) => r.id === id)?.stride === ans).length;

    return { total, found, pct, strideCovered, answered, correct };
  }, [allRisks, discoveredRisks, strideAnswers, discoveredIds]);

  const recommendations = useMemo(() => {
    const missing = allRisks.filter((r) => !discoveredIds.has(r.id));
    if (missing.length === 0) return [];

    const score = new Map();
    for (const m of METHODS) score.set(m.key, 0);

    for (const r of missing) {
      for (const m of r.foundBy) {
        score.set(m, (score.get(m) ?? 0) + 1);
      }
    }

    const ranked = METHODS
      .filter((m) => !selectedMethods.has(m.key))
      .map((m) => ({ key: m.key, label: m.label, count: score.get(m.key) ?? 0 }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    return ranked;
  }, [allRisks, discoveredIds, selectedMethods]);

  const toggleMethod = (key) => {
    setSelectedMethods((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setStride = (riskId, val) => {
    // sobald man ändert, ist "checked" eigentlich nicht mehr aktuell
    // (damit "Prüfen" wieder Sinn ergibt)
    setStrideChecked(false);
    setStrideReveal(false);
    setStrideAnswers((prev) => ({ ...prev, [riskId]: val }));
  };

  const toggleTreeChoice = (pathId) => {
    setTreeChoices((prev) => ({ ...prev, [pathId]: !prev[pathId] }));
  };

  const resetAll = () => {
    setSelectedMethods(new Set());
    setStrideAnswers({});
    setTreeChoices({});
    setTab("methods");
    setStrideChecked(false);
    setStrideReveal(false);
  };

  const strideName = (key) => STRIDE.find((s) => s.key === key)?.name ?? key;

  const label =
    selectedMethods.size === 0
      ? "Starte mit 1–2 Methoden, um Risiken zu „sehen“."
      : selectedMethods.has("tm")
      ? "Threat Modeling aktiv: systematischer Blick (alle Risiken sichtbar)."
      : "Mehr Methoden = weniger blinde Flecken.";

  return (
    <figure className="ril">
      <style>{css}</style>

      <div className="rilCard">
        <div className="rilHeader">
          <div>
            <h3 className="rilTitle">{title}</h3>
            
          </div>

          <div className="rilRow">
            <select
              className="rilSelect"
              value={scenarioKey}
              onChange={(e) => setScenarioKey(e.target.value)}
              aria-label="Szenario wählen"
            >
              {SCENARIOS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>

            <button type="button" className="rilBtn" onClick={resetAll}>
              Reset
            </button>
          </div>
        </div>

        <div className="rilTabs" role="tablist" aria-label="Lernbereiche">
          <button
            type="button"
            className={`rilTabBtn ${tab === "methods" ? "rilTabBtnActive" : ""}`}
            onClick={() => setTab("methods")}
            role="tab"
            aria-selected={tab === "methods"}
          >
            1) Methoden
          </button>
          <button
            type="button"
            className={`rilTabBtn ${tab === "stride" ? "rilTabBtnActive" : ""}`}
            onClick={() => setTab("stride")}
            role="tab"
            aria-selected={tab === "stride"}
          >
            2) STRIDE einordnen
          </button>
          <button
            type="button"
            className={`rilTabBtn ${tab === "tree" ? "rilTabBtnActive" : ""}`}
            onClick={() => setTab("tree")}
            role="tab"
            aria-selected={tab === "tree"}
          >
            3) Attack Tree
          </button>
        </div>

        {tab === "methods" && (
          <div className="rilGrid">
            <div className="rilPanel">
              <div className="rilPanelTitle">Methoden auswählen</div>

              <div className="rilHint">{label}</div>

              <div className="rilMethods" style={{ marginTop: 10 }}>
                {METHODS.map((m) => (
                  <div className="rilMethod" key={m.key}>
                    <div className="rilMethodLeft">
                      <div className="rilMethodLabel">{m.label}</div>
                      <div className="rilMethodShort">{m.short}</div>
                    </div>

                    <div className="rilToggle">
                      <span className="rilPill">{selectedMethods.has(m.key) ? "aktiv" : "aus"}</span>
                      <button
                        type="button"
                        className="rilBtn"
                        onClick={() => toggleMethod(m.key)}
                        aria-pressed={selectedMethods.has(m.key)}
                      >
                        {selectedMethods.has(m.key) ? "Deaktivieren" : "Aktivieren"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rilStats">
                <div className="rilStat">
                  <div className="rilStatTitle">Entdeckte Risiken</div>
                  <div className="rilStatValue">
                    {progress.found} / {progress.total} ({progress.pct}%)
                  </div>
                  <div className="rilBar" aria-hidden="true">
                    <div className="rilBarFill" style={{ width: `${progress.pct}%` }} />
                  </div>
                </div>

                <div className="rilStat">
                  <div className="rilStatTitle">STRIDE‑Abdeckung (gefunden)</div>
                  <div className="rilStatValue">{progress.strideCovered} / 6</div>
                  <div className="rilHint">Je mehr Kategorien du abdeckst, desto vollständiger ist dein Blick.</div>
                </div>

                <div className="rilStat">
                  <div className="rilStatTitle">Empfehlung (wenn noch Lücken)</div>
                  {recommendations.length === 0 ? (
                    <div className="rilStatValue">Alles gefunden </div>
                  ) : (
                    <>
                      <div className="rilStatValue">
                        {recommendations.map((r, i) => (
                          <span key={r.key}>
                            {r.label}
                            {i < recommendations.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </div>
                      <div className="rilHint">Diese Methoden würden dir die meisten fehlenden Risiken aufdecken.</div>
                    </>
                  )}
                </div>
              </div>

              <div className="rilMini">
                <div className="rilPanelTitle">„Kronjuwelen“ im Szenario</div>
                <div className="rilHint">
                  Startpunkt für Identifikation: Was ist besonders schützenswert?
                </div>
                <div className="rilRow" style={{ marginTop: 8 }}>
                  {scenario.crownJewels.map((x) => (
                    <span key={x} className="rilPill">
                      {x}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="rilPanel">
              <div className="rilPanelTitle">Risikoregister (automatisch gefüllt)</div>

              {discoveredRisks.length === 0 ? (
                <div className="rilHint">Aktiviere mindestens eine Methode – dann erscheinen typische Risiken.</div>
              ) : (
                <ul className="rilList">
                  {discoveredRisks.map((r) => (
                    <li key={r.id} className="rilRisk">
                      <div className="rilRiskTop">
                        <div className="rilRiskTitle">{r.title}</div>
                        <span className="rilPill">Kategorie: STRIDE</span>
                      </div>
                      <div className="rilRiskDesc">{r.desc}</div>
                      <div className="rilRiskMeta">
                        Gefunden durch:{" "}
                        {r.foundBy
                          .map((k) => METHODS.find((m) => m.key === k)?.label ?? k)
                          .join(", ")}
                        {" • "}Hinweis: {r.note}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="rilHint" style={{ marginTop: 10 }}>
                Tipp: Kombiniere Methoden. Jede Methode hat „blinde Flecken“.
              </div>
            </div>
          </div>
        )}

        {tab === "stride" && (
          <div className="rilGrid">
            <div className="rilPanel">
              <div className="rilPanelTitle">STRIDE‑Sortieraufgabe</div>
              <div className="rilHint">
                Ordne jedes gefundene Risiko einer STRIDE‑Kategorie zu. <strong>Lösungen werden erst nach „Antworten prüfen“ sichtbar.</strong>
                (Wenn du noch keine Risiken hast: gehe zu „Methoden“ und aktiviere etwas.)
              </div>

              <div className="rilRow" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className={`rilBtn ${strideChecked ? "" : "rilBtnPrimary"}`}
                  onClick={() => setStrideChecked(true)}
                  disabled={discoveredRisks.length === 0}
                >
                  Antworten prüfen
                </button>

                <button
                  type="button"
                  className={`rilBtn ${strideReveal ? "rilBtnPrimary" : ""}`}
                  onClick={() => setStrideReveal((v) => !v)}
                  disabled={!strideChecked}
                  title="Zeigt die richtige STRIDE-Kategorie an (nur nach dem Prüfen)."
                >
                  {strideReveal ? "Lösungen verbergen" : "Lösungen anzeigen"}
                </button>

                <button
                  type="button"
                  className="rilBtn"
                  onClick={() => {
                    setStrideChecked(false);
                    setStrideReveal(false);
                    setStrideAnswers({});
                  }}
                  disabled={discoveredRisks.length === 0}
                >
                  Neu starten
                </button>
              </div>

              {discoveredRisks.length === 0 ? (
                <div className="rilHint" style={{ marginTop: 10 }}>
                  Noch nichts gefunden. Starte mit z. B. „Checklisten“ oder „Workshop“.
                </div>
              ) : (
                <ul className="rilList" style={{ marginTop: 10 }}>
                  {discoveredRisks.map((r) => {
                    const ans = strideAnswers[r.id];
                    const correct = ans && ans === r.stride;

                    return (
                      <li key={r.id} className="rilRisk">
                        <div className="rilRiskTop">
                          <div className="rilRiskTitle">{r.title}</div>
                          <span className="rilPill">STRIDE zuordnen</span>
                        </div>
                        <div className="rilRiskDesc">{r.desc}</div>

                        <div className="rilStrideBox">
                          <div style={{ fontWeight: 800, opacity: 0.9 }}>Deine Zuordnung:</div>

                          <div className="rilStrideOptions">
                            {STRIDE.map((s) => (
                              <button
                                key={s.key}
                                type="button"
                                className={`rilOpt ${ans === s.key ? "rilOptActive" : ""}`}
                                onClick={() => setStride(r.id, s.key)}
                                aria-pressed={ans === s.key}
                              >
                                {s.key} – {s.name}
                              </button>
                            ))}
                          </div>

                          {!strideChecked && (
                            <div className="rilFeedbackNeutral">
                              Hinweis: Erst „Antworten prüfen“ klicken, um Feedback zu sehen.
                            </div>
                          )}

                          {strideChecked && !ans && (
                            <div className="rilFeedbackBad">✗ Keine Antwort gewählt.</div>
                          )}

                          {strideChecked && ans && correct && (
                            <div className="rilFeedbackOk">✓ Passt.</div>
                          )}

                          {strideChecked && ans && !correct && !strideReveal && (
                            <div className="rilFeedbackBad">
                              ✗ Nicht ganz. Tipp: Schau in den STRIDE‑Spickzettel (rechts) und überlege, worum es „im Kern“ geht.
                            </div>
                          )}

                          {strideChecked && ans && !correct && strideReveal && (
                            <div className="rilFeedbackBad">
                              ✗ Nicht ganz. Richtige Kategorie: <strong>{r.stride} – {strideName(r.stride)}</strong>
                              <div style={{ marginTop: 4, opacity: 0.9 }}>
                                Erklärung: {STRIDE.find((x) => x.key === r.stride)?.help}
                              </div>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="rilStats">
                <div className="rilStat">
                  <div className="rilStatTitle">Beantwortet</div>
                  <div className="rilStatValue">
                    {progress.answered} / {progress.found}
                  </div>
                </div>
                <div className="rilStat">
                  <div className="rilStatTitle">Richtig</div>
                  <div className="rilStatValue">
                    {strideChecked ? `${progress.correct} / ${progress.found}` : "—"}
                  </div>
                  <div className="rilHint">Wird nach „Antworten prüfen“ angezeigt.</div>
                </div>
              </div>
            </div>

            <div className="rilPanel">
              <div className="rilPanelTitle">STRIDE‑Spickzettel</div>
              <div className="rilStrideLegend">
                {STRIDE.map((s) => (
                  <div key={s.key} className="rilLegendItem">
                    <span className="rilLegendKey">{s.key}</span>
                    <strong>{s.name}</strong>
                    <div className="rilHint">{s.help}</div>
                  </div>
                ))}
              </div>

              <div className="rilMini">
                <div className="rilPanelTitle">Warum das bei Identifikation hilft</div>
                <div className="rilHint">
                  STRIDE ist eine Denk‑Checkliste: Wenn du jede Kategorie einmal durchgehst,
                  übersiehst du weniger Bedrohungen.
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "tree" && (
          <div className="rilGrid">
            <div className="rilPanel">
              <div className="rilPanelTitle">Attack‑Tree‑Miniübung</div>
              <div className="rilHint">
                {scenario.attackTree.goalLabel}. {scenario.attackTree.question}
              </div>

              <div className="rilMini">
                <div className="rilPanelTitle">Markiere alle plausiblen Pfade</div>

                <ul className="rilList">
                  {scenario.attackTree.paths.map((p) => {
                    const chosen = !!treeChoices[p.id];
                    const showFeedback = Object.keys(treeChoices).length > 0;

                    return (
                      <li key={p.id} className="rilRisk">
                        <div className="rilRiskTop">
                          <div className="rilRiskTitle">{p.label}</div>
                          <button
                            type="button"
                            className="rilBtn"
                            onClick={() => toggleTreeChoice(p.id)}
                            aria-pressed={chosen}
                          >
                            {chosen ? "Markiert" : "Markieren"}
                          </button>
                        </div>

                        <div className="rilRiskMeta">
                          Bezieht sich auf Risiken:{" "}
                          {p.relatedRiskIds
                            .map((id) => scenario.risks.find((r) => r.id === id)?.title ?? id)
                            .join(", ")}
                        </div>

                        {showFeedback && chosen && p.correct && (
                          <div className="rilFeedbackOk">✓ Plausibel. {p.why}</div>
                        )}
                        {showFeedback && chosen && !p.correct && (
                          <div className="rilFeedbackBad">✗ Eher nicht. {p.why}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>

                <div className="rilHint" style={{ marginTop: 10 }}>
                  Lernpunkt: Ein Attack Tree zeigt oft mehrere alternative Wege zum Ziel. Für Risikoidentifikation heißt das:
                  Du suchst systematisch nach möglichen Pfaden – nicht nur nach „dem einen“ Problem.
                </div>
              </div>
            </div>

            <div className="rilPanel">
              <div className="rilPanelTitle">Brücke zurück zur Identifikation</div>
              <div className="rilHint">
                Wenn dir hier Pfade fehlen oder unklar sind, ist das ein Signal:
              </div>
              <ul className="rilHint" style={{ marginTop: 8, paddingLeft: 18 }}>
                <li>Nutze zusätzliche Methoden (z. B. Prozessanalyse + Scan).</li>
                <li>Starte bei den „Kronjuwelen“: Was wäre am schützenswertesten?</li>
                <li>STRIDE hilft, Denk‑Lücken zu schließen.</li>
              </ul>

              <div className="rilMini">
                <div className="rilPanelTitle">Shortcut im Tool</div>
                <div className="rilHint">
                  Wenn du bei „Methoden“ Threat Modeling aktivierst, werden alle Risiken sichtbar – das ist die Idee:
                  einmal systematisch komplett durchdenken.
                </div>
              </div>
            </div>
          </div>
        )}

      

        {/* Mini‑Legende */}
        <div className="rilLegendBottom">
          <details>
            <summary>Mini‑Legende (Begriffe)</summary>
            <ul>
              <li><strong>Kronjuwelen</strong>: besonders schützenswerte Assets (Daten/Funktionen/Prozesse).</li>
              <li><strong>Risikoregister</strong>: dokumentierte Liste identifizierter Risiken (Titel + Beschreibung + Kontext).</li>
              <li><strong>Threat Modeling</strong>: systematisches Vorgehen, um Bedrohungen/Schwachstellen zu finden.</li>
              <li><strong>STRIDE</strong>: Kategorien, um Bedrohungen zu sortieren (S/T/R/I/D/E).</li>
              <li><strong>Attack Tree</strong>: Baum/Wege‑Denken: mehrere Pfade können zum selben Ziel führen.</li>
              <li><strong>Scan/Pentest</strong>: technische Prüfungen, die Hinweise auf Schwachstellen geben (ohne „Allwissens‑Garantie“).</li>
              <li><strong>Checkliste</strong>: Standardfragen, damit häufige Risiken nicht vergessen werden.</li>
              <li><strong>Workshop/Interviews</strong>: Wissen aus verschiedenen Rollen/Abteilungen zusammenbringen.</li>
            </ul>
          </details>
        </div>
      </div>
    </figure>
  );
}